import React, { useState } from "react";
import { TextField, Card, OutlinedInput } from "@mui/material";
import { useForm } from "react-hook-form";
import { Link, useNavigate } from "react-router-dom";
import useAuth from "hooks/useAuth";
import BasicLayout from "layouts/authentication/components/BasicLayout";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import bgImage from "assets/images/bg-sign-in-basic.jpeg";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";

const ForgotPassword = () => {
  const navigate = useNavigate();
  const { register, handleSubmit } = useForm();
  const { forgotPasswordOTPRequest, verifyOTPRequest, setPassword } = useAuth();
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [showPassword, setShowPassword] = React.useState(false);

  const handleClickShowPassword = () => setShowPassword((show) => !show);

  const handleEmailSubmit = async (data) => {
    try {
      const response = await forgotPasswordOTPRequest({ email: data.email });
      if (response.success) {
        setEmail(data.email);
        setStep(2);
        setErrorMessage(""); // Clear error message
      } else {
        setErrorMessage(response?.message || "Error sending OTP");
      }
    } catch (error) {
      setErrorMessage(error.response?.data?.message || "Error sending OTP");
    }
  };

  const handleOtpSubmit = async (data) => {
    try {
      const response = await verifyOTPRequest({ email, otp: data.otp });
      if (response.success) {
        setStep(3);
        setErrorMessage("");
      } else {
        setErrorMessage(response.message || "Invalid OTP");
      }
    } catch (error) {
      setErrorMessage(error.response?.data?.message || "Invalid OTP");
    }
  };

  const handlePasswordSubmit = async (data) => {
    try {
      const response = await setPassword({
        email,
        password: data.password,
        forcePasswordReset: false,
      });

      if (response.success) {
        alert("Password reset successful!");
        setStep(1);
        navigate("/authentication/sign-in");
        setErrorMessage("");
      } else {
        setErrorMessage(response.message || "Error resetting password");
      }
    } catch (error) {
      setErrorMessage(error.response?.data?.message || "Error resetting password");
    }
  };

  return (
    <BasicLayout image={bgImage}>
      <Card>
        <MDBox
          variant="gradient"
          bgColor="info"
          borderRadius="lg"
          coloredShadow="info"
          mx={2}
          mt={-3}
          p={2}
          mb={1}
          textAlign="center"
        >
          <MDTypography variant="h5" color="white">
            {step === 1 && "Forgot Password"}
            {step === 2 && "Verify OTP"}
            {step === 3 && "Reset Password"}
          </MDTypography>
        </MDBox>
        <MDBox pt={4} pb={3} px={3}>
          {errorMessage && (
            <MDBox textAlign="center" color="error" bgcolor="error.light" borderRadius="md">
              <MDTypography color="error">{errorMessage}</MDTypography>
            </MDBox>
          )}

          {step === 1 && (
            <form onSubmit={handleSubmit(handleEmailSubmit)}>
              <MDTypography variant="body2">Email</MDTypography>
              <TextField fullWidth {...register("email", { required: true })} margin="normal" />
              <MDBox mt={3} mb={1}>
                <MDButton type="submit" variant="gradient" color="info" fullWidth>
                  Send OTP
                </MDButton>
              </MDBox>
              <MDBox textAlign="center">
                <MDTypography variant="button" color="text">
                  <Link
                    to="/authentication/sign-in"
                    style={{ color: "#1976D2", fontWeight: "bold" }}
                  >
                    Sign In?
                  </Link>
                </MDTypography>
              </MDBox>
            </form>
          )}

          {step === 2 && (
            <form onSubmit={handleSubmit(handleOtpSubmit)}>
              <MDTypography variant="body2">Enter OTP</MDTypography>
              <TextField
                fullWidth
                inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
                {...register("otp", {
                  required: "OTP is required",
                  pattern: {
                    value: /^[0-9]{6}$/,
                    message: "OTP must be a 6-digit number",
                  },
                })}
                type="text"
                margin="normal"
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, ""); // Remove non-numeric characters
                  if (value.length <= 6) {
                    e.target.value = value; // Allow only up to 6 digits
                  } else {
                    e.target.value = value.slice(0, 6); // Trim extra digits
                  }
                }}
              />
              <MDBox mt={3} mb={1}>
                <MDButton type="submit" variant="gradient" color="info" fullWidth>
                  Verify OTP
                </MDButton>
              </MDBox>
              <MDBox textAlign="center">
                <MDTypography variant="button" color="text">
                  <Link
                    to="/authentication/sign-in"
                    style={{ color: "#1976D2", fontWeight: "bold" }}
                  >
                    Sign In?
                  </Link>
                </MDTypography>
              </MDBox>
            </form>
          )}

          {step === 3 && (
            <form onSubmit={handleSubmit(handlePasswordSubmit)}>
              <MDTypography variant="body2">New Password</MDTypography>
              <OutlinedInput
                fullWidth
                {...register("password", { required: true })}
                margin="normal"
                type={showPassword ? "text" : "password"}
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton onClick={handleClickShowPassword}>
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                }
              />
              <MDBox mt={3} mb={1}>
                <MDButton type="submit" variant="gradient" color="info" fullWidth>
                  Reset Password
                </MDButton>
              </MDBox>
              <MDBox textAlign="center">
                <MDTypography variant="button" color="text">
                  <Link
                    to="/authentication/sign-in"
                    style={{ color: "#1976D2", fontWeight: "bold" }}
                  >
                    Sign In?
                  </Link>
                </MDTypography>
              </MDBox>
            </form>
          )}
        </MDBox>
      </Card>
    </BasicLayout>
  );
};

export default ForgotPassword;
